<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/#/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Track Application</span>
            </nav>
            <h1 class="page-title">Track Your Application</h1>
            <p class="page-subtitle">Monitor the progress of your building permit application</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">

          <!-- Search Card -->
          <div class="search-card">
            <div class="search-card-header">
              <div class="search-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
              <div>
                <h2>Enter Application Number</h2>
                <p>Enter your application reference number to view the status</p>
              </div>
            </div>
            <div class="search-form">
              <div class="search-input-group">
                <input
                    type="text"
                    v-model="applicationNumber"
                    class="search-input"
                    placeholder="e.g., ECON-20260121-D1B860"
                    @keyup.enter="trackApplication"
                />
                <button
                    class="search-btn"
                    @click="trackApplication"
                    :disabled="!applicationNumber.trim() || loading"
                >
                  <span v-if="loading" class="spinner"></span>
                  <template v-else>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    Track Application
                  </template>
                </button>
              </div>
              <p v-if="error" class="error-message">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                {{ error }}
              </p>
            </div>
          </div>

          <!-- Application Details (shown after search) -->
          <div v-if="applicationData" class="application-details">

            <!-- Status Overview Card -->
            <div class="status-overview-card">
              <div class="status-header">
                <div class="app-info">
                  <span class="app-number">{{ applicationData.application_number }}</span>
                  <span class="app-title">{{ applicationData.project_title }}</span>
                </div>
                <div class="status-badge-wrapper">
                  <span class="status-badge" :class="getStatusClass(applicationData.status)">
                    {{ formatStatus(applicationData.status) }}
                  </span>
                </div>
              </div>

              <!-- Progress Timeline -->
              <div class="progress-timeline">
                <div
                    v-for="(stage, index) in workflowStages"
                    :key="index"
                    class="timeline-item"
                    :class="getStageClass(stage.key)"
                >
                  <div class="timeline-connector" v-if="index > 0"></div>
                  <div class="timeline-icon">
                    <svg v-if="isStageCompleted(stage.key)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <div class="timeline-content">
                    <strong>{{ stage.label }}</strong>
                    <span v-if="getStageDate(stage.key)">{{ formatDate(getStageDate(stage.key)) }}</span>
                    <span v-else class="pending-text">Pending</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details Grid -->
            <div class="details-grid">

              <!-- Project Information -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M2 20h20M5 20V8l7-5 7 5v12"></path>
                  </svg>
                  <h3>Project Information</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">Development Type</span>
                    <span class="detail-value">{{ formatDevelopmentType(applicationData.development_type) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Ownership Type</span>
                    <span class="detail-value">{{ applicationData.ownership_type || '-' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Project Value</span>
                    <span class="detail-value">KES {{ formatAmount(applicationData.project_value) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Plot/LR Number</span>
                    <span class="detail-value">{{ applicationData.plot_number }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Plot Size</span>
                    <span class="detail-value">{{ applicationData.plot_size ? applicationData.plot_size + ' m²' : '-' }}</span>
                  </div>
                  <div class="detail-row" v-if="applicationData.project_description">
                    <span class="detail-label">Description</span>
                    <span class="detail-value">{{ applicationData.project_description }}</span>
                  </div>
                </div>
              </div>

              <!-- Property Owner -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <h3>Property Owner</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">Full Name</span>
                    <span class="detail-value">{{ applicationData.owner_full_names }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">ID Number</span>
                    <span class="detail-value">{{ applicationData.owner_id_number }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Phone</span>
                    <span class="detail-value">{{ applicationData.owner_phone_number }}</span>
                  </div>
                  <div class="detail-row" v-if="applicationData.owner_email">
                    <span class="detail-label">Email</span>
                    <span class="detail-value">{{ applicationData.owner_email }}</span>
                  </div>
                  <div class="detail-row" v-if="applicationData.owner_address">
                    <span class="detail-label">Address</span>
                    <span class="detail-value">{{ applicationData.owner_address }}</span>
                  </div>
                </div>
              </div>

              <!-- Professional Details -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                  <h3>Professional Details</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">Name</span>
                    <span class="detail-value">{{ applicationData.professional_name || '-' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Type</span>
                    <span class="detail-value">{{ formatProfessionalType(applicationData.professional_type) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Certificate No.</span>
                    <span class="detail-value">{{ applicationData.certificate_number || '-' }}</span>
                  </div>
                  <div class="detail-row" v-if="applicationData.professional_phone">
                    <span class="detail-label">Phone</span>
                    <span class="detail-value">{{ applicationData.professional_phone }}</span>
                  </div>
                </div>
              </div>

              <!-- Fees & Payment -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <h3>Fees & Payment</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">Bill Number</span>
                    <span class="detail-value bill-number">{{ applicationData.billNo || '-' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Total Fees</span>
                    <span class="detail-value">KES {{ formatAmount(totalFees) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Amount Paid</span>
                    <span class="detail-value text-success">KES {{ formatAmount(totalPaid) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Balance</span>
                    <span class="detail-value" :class="balance > 0 ? 'text-danger' : 'text-success'">
                      KES {{ formatAmount(balance) }}
                    </span>
                  </div>
                  <div class="payment-status-badge" :class="balance > 0 ? 'unpaid' : 'paid'">
                    {{ balance > 0 ? 'Payment Required' : 'Fully Paid' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Workflow History -->
            <div class="workflow-card" v-if="workflow && workflow.length > 0">
              <div class="workflow-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
                <h3>Application History</h3>
              </div>
              <div class="workflow-timeline">
                <div
                    v-for="(item, index) in workflow"
                    :key="index"
                    class="workflow-item"
                    :class="item.status"
                >
                  <div class="workflow-icon">
                    <svg v-if="item.status === 'approved'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <svg v-else-if="item.status === 'rejected'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    <svg v-else-if="item.status === 'in_progress'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                    </svg>
                  </div>
                  <div class="workflow-content">
                    <div class="workflow-title">
                      <strong>{{ formatStageName(item.stage) }}</strong>
                      <span class="workflow-status" :class="item.status">{{ formatStatus(item.status) }}</span>
                    </div>
                    <p v-if="item.comments" class="workflow-comments">{{ item.comments }}</p>
                    <p v-if="item.assigned_to" class="workflow-assigned">Assigned to: {{ item.assigned_to }}</p>
                    <span class="workflow-date">{{ formatDateTime(item.action_date || item.created_at) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents -->
            <div class="documents-card" v-if="documents && documents.length > 0">
              <div class="documents-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <h3>Submitted Documents</h3>
              </div>
              <div class="documents-grid">
                <div v-for="doc in documents" :key="doc.id" class="document-item">
                  <div class="doc-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                  </div>
                  <div class="doc-info">
                    <strong>{{ formatDocumentType(doc.document_type) }}</strong>
                    <span>{{ doc.document_name }}</span>
                  </div>
                  <span class="doc-status" :class="doc.status">{{ doc.status }}</span>
                </div>
              </div>
            </div>

            <!-- Inspections -->
            <div class="inspections-card" v-if="inspections && inspections.length > 0">
              <div class="inspections-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <h3>Scheduled Inspections</h3>
              </div>
              <div class="inspections-list">
                <div v-for="inspection in inspections" :key="inspection.id" class="inspection-item">
                  <div class="inspection-date">
                    <span class="day">{{ getDay(inspection.scheduled_date) }}</span>
                    <span class="month">{{ getMonth(inspection.scheduled_date) }}</span>
                  </div>
                  <div class="inspection-info">
                    <strong>{{ inspection.inspection_type }}</strong>
                    <span v-if="inspection.inspector_name">Inspector: {{ inspection.inspector_name }}</span>
                    <span class="inspection-status" :class="inspection.status">{{ inspection.status }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="actions-card">
              <button class="btn btn-primary" @click="printAcknowledgment">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6 9 6 2 18 2 18 9"></polyline>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                  <rect x="6" y="14" width="12" height="8"></rect>
                </svg>
                Print Acknowledgment
              </button>
              <button class="btn btn-secondary" @click="resetSearch">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                New Search
              </button>
            </div>

          </div>

        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { execute, econstruction } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "TrackApplication",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      applicationNumber: '',
      loading: false,
      error: '',
      applicationData: null,
      documents: [],
      fees: [],
      payments: [],
      finalFees: [],
      finalPayments: [],
      workflow: [],
      inspections: [],
      workflowStages: [
        { key: 'submission', label: 'Application Submitted' },
        { key: 'payment', label: 'Payment' },
        { key: 'review', label: 'Under Review' },
        { key: 'inspection', label: 'Site Inspection' },
        { key: 'approval', label: 'Approval' },
        { key: 'permit_issued', label: 'Permit Issued' }
      ]
    };
  },
  computed: {
    totalFees() {
      let total = 0;
      if (this.fees && this.fees.length > 0) {
        total += this.fees.reduce((sum, fee) => sum + parseFloat(fee.detailAmount || 0), 0);
      }
      if (this.finalFees && this.finalFees.length > 0) {
        total += this.finalFees.reduce((sum, fee) => sum + parseFloat(fee.detailAmount || 0), 0);
      }
      return total;
    },
    totalPaid() {
      let total = 0;
      if (this.payments && this.payments.length > 0) {
        total += this.payments.reduce((sum, p) => sum + parseFloat(p.receiptAmount || 0), 0);
      }
      if (this.finalPayments && this.finalPayments.length > 0) {
        total += this.finalPayments.reduce((sum, p) => sum + parseFloat(p.receiptAmount || 0), 0);
      }
      return total;
    },
    balance() {
      return this.totalFees - this.totalPaid;
    }
  },
  mounted() {
    // Check if application number was passed via route params (URL path)
    // Example: /#/track-application/ECON-20260121-D1B860
    if (this.$route.params.applicationNumber) {
      this.applicationNumber = this.$route.params.applicationNumber;
      this.trackApplication();
    }
        // Also check query params for backward compatibility
    // Example: /#/track-application?applicationNumber=ECON-20260121-D1B860
    else if (this.$route.query.applicationNumber) {
      this.applicationNumber = this.$route.query.applicationNumber;
      this.trackApplication();
    }
  },
  // Watch for route changes (when navigating with different application numbers)
  watch: {
    '$route.params.applicationNumber': {
      handler(newVal) {
        if (newVal) {
          this.applicationNumber = newVal;
          this.trackApplication();
        }
      }
    },
    '$route.query.applicationNumber': {
      handler(newVal) {
        if (newVal && !this.$route.params.applicationNumber) {
          this.applicationNumber = newVal;
          this.trackApplication();
        }
      }
    }
  },
  methods: {
    async trackApplication() {
      if (!this.applicationNumber.trim()) {
        this.error = 'Please enter an application number';
        return;
      }

      this.loading = true;
      this.error = '';
      this.applicationData = null;

      try {
        const data = new FormData();
        data.append("function", "getApplicationByNumber");
        data.append("applicationNumber", this.applicationNumber.trim());

        const res = await execute(data, econstruction);

        if (res.data.success) {
          this.applicationData = res.data.data.application;
          this.documents = res.data.data.documents || [];
          this.fees = res.data.data.fees || [];
          this.payments = res.data.data.payments || [];
          this.finalFees = res.data.data.finalFees || [];
          this.finalPayments = res.data.data.finalPayments || [];
          this.workflow = res.data.data.workflow || [];
          this.inspections = res.data.data.inspections || [];

          // Update URL to include application number (without reloading)
          if (this.$route.params.applicationNumber !== this.applicationNumber) {
            this.$router.replace({
              name: 'TrackApplication',
              params: { applicationNumber: this.applicationNumber }
            }).catch(() => {});
          }
        } else {
          this.error = res.data.message || 'Application not found. Please check the application number.';
        }
      } catch (e) {
        console.error('Error tracking application:', e);
        this.error = 'An error occurred while fetching application details. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    resetSearch() {
      this.applicationNumber = '';
      this.applicationData = null;
      this.documents = [];
      this.fees = [];
      this.payments = [];
      this.finalFees = [];
      this.finalPayments = [];
      this.workflow = [];
      this.inspections = [];
      this.error = '';

      // Update URL to remove application number
      this.$router.replace({ name: 'TrackApplication' }).catch(() => {});
    },

    printAcknowledgment() {
      const receiptData = {
        applicationNumber: this.applicationData.application_number
      };
      localStorage.setItem('applicationReceipt', JSON.stringify(receiptData));
      const routeData = this.$router.resolve({ name: 'ApplicationAcknowledgment' });
      window.open(routeData.href, '_blank');
    },

    isStageCompleted(stageKey) {
      const stage = this.workflow.find(w => w.stage === stageKey);
      return stage && stage.status === 'approved';
    },

    getStageClass(stageKey) {
      const stage = this.workflow.find(w => w.stage === stageKey);
      if (!stage) return '';
      if (stage.status === 'approved') return 'completed';
      if (stage.status === 'in_progress') return 'active';
      if (stage.status === 'rejected') return 'rejected';
      return 'pending';
    },

    getStageDate(stageKey) {
      const stage = this.workflow.find(w => w.stage === stageKey);
      return stage ? (stage.action_date || stage.created_at) : null;
    },

    getStatusClass(status) {
      const classes = {
        'pending': 'status-pending',
        'in_progress': 'status-progress',
        'under_review': 'status-review',
        'approved': 'status-approved',
        'rejected': 'status-rejected',
        'completed': 'status-completed'
      };
      return classes[status] || 'status-pending';
    },

    formatStatus(status) {
      const statuses = {
        'pending': 'Pending',
        'in_progress': 'In Progress',
        'under_review': 'Under Review',
        'approved': 'Approved',
        'rejected': 'Rejected',
        'completed': 'Completed'
      };
      return statuses[status] || status || 'Pending';
    },

    formatStageName(stage) {
      const stages = {
        'submission': 'Application Submitted',
        'payment': 'Payment Processing',
        'review': 'Technical Review',
        'inspection': 'Site Inspection',
        'approval': 'Final Approval',
        'permit_issued': 'Permit Issued'
      };
      return stages[stage] || stage;
    },

    formatDevelopmentType(type) {
      const types = {
        'residential': 'Residential',
        'commercial': 'Commercial',
        'industrial': 'Industrial',
        'institutional': 'Institutional',
        'newBuildings': 'New Buildings',
        'buildingAlterations': 'Alterations and Additions'
      };
      return types[type] || type || '-';
    },

    formatProfessionalType(type) {
      const types = {
        'architect': 'Architect',
        'civilEngineer': 'Civil Engineer',
        'structuralEngineer': 'Structural Engineer',
        'electricalEngineer': 'Electrical Engineer',
        'mechanicalEngineer': 'Mechanical Engineer',
        'landSurveyor': 'Land Surveyor',
        'urbanPlanner': 'Urban Planner'
      };
      return types[type] || type || '-';
    },

    formatDocumentType(type) {
      const types = {
        'plupaDC1A': 'PLUPA Form DC/1A',
        'plupaDC1B': 'PLUPA Form DC/1B',
        'landOwnership': 'Title Deed / Land Ownership',
        'officialSearch': 'Certificate of Official Search',
        'identificationCard': 'Owner\'s ID Card',
        'kraPin': 'KRA PIN Certificate',
        'architecturalDrawings': 'Architectural Drawings',
        'structuralDrawings': 'Structural Drawings',
        'sitePlan': 'Site Plan / Survey Plan',
        'surveyPlan': 'Survey Plan (Survey of Kenya)',
        'ratesReceipt': 'Rates Clearance Certificate'
      };
      return types[type] || type;
    },

    formatAmount(amount) {
      if (!amount) return '0.00';
      return new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2 }).format(parseFloat(amount));
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    getDay(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).getDate();
    },

    getMonth(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-GB', { month: 'short' });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; }
.main-content { flex: 1; padding-top: 72px; }
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); padding: 48px 0; color: white; }
.container { max-width: 1000px; margin: 0 auto; padding: 0 24px; }
.breadcrumb-nav { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; font-size: 14px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }
.page-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 32px; font-weight: 800; margin-bottom: 8px; }
.page-subtitle { opacity: 0.9; font-size: 16px; }

.content-section { padding: 48px 0 80px; }

/* Search Card */
.search-card { background: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); margin-bottom: 32px; }
.search-card-header { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
.search-icon { width: 56px; height: 56px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.search-card-header h2 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
.search-card-header p { font-size: 14px; color: #64748b; margin: 0; }
.search-input-group { display: flex; gap: 12px; }
.search-input { flex: 1; padding: 16px 20px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 16px; font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
.search-input:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1); }
.search-input::placeholder { color: #94a3b8; }
.search-btn { display: flex; align-items: center; gap: 10px; padding: 16px 28px; background: #0f4c81; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background 0.2s; }
.search-btn:hover:not(:disabled) { background: #0a3459; }
.search-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.error-message { display: flex; align-items: center; gap: 8px; margin-top: 12px; color: #dc2626; font-size: 14px; }

/* Status Overview Card */
.status-overview-card { background: white; border-radius: 16px; padding: 28px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); margin-bottom: 24px; }
.status-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; flex-wrap: wrap; gap: 16px; }
.app-info { display: flex; flex-direction: column; gap: 4px; }
.app-number { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 800; color: #0f4c81; }
.app-title { font-size: 14px; color: #64748b; }
.status-badge { display: inline-block; padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 600; text-transform: uppercase; }
.status-pending { background: #fef3c7; color: #92400e; }
.status-progress { background: #dbeafe; color: #1e40af; }
.status-review { background: #e0e7ff; color: #3730a3; }
.status-approved { background: #d1fae5; color: #065f46; }
.status-rejected { background: #fee2e2; color: #991b1b; }
.status-completed { background: #d1fae5; color: #065f46; }

/* Progress Timeline */
.progress-timeline { display: flex; justify-content: space-between; gap: 8px; overflow-x: auto; padding-bottom: 8px; }
.timeline-item { display: flex; flex-direction: column; align-items: center; gap: 8px; min-width: 100px; flex: 1; position: relative; }
.timeline-connector { position: absolute; top: 18px; right: 50%; width: 100%; height: 3px; background: #e2e8f0; z-index: 0; }
.timeline-item.completed .timeline-connector { background: #10b981; }
.timeline-item.active .timeline-connector { background: linear-gradient(90deg, #10b981 50%, #e2e8f0 50%); }
.timeline-icon { width: 36px; height: 36px; border-radius: 50%; background: #e2e8f0; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; color: #64748b; z-index: 1; }
.timeline-item.completed .timeline-icon { background: #10b981; color: white; }
.timeline-item.active .timeline-icon { background: #0f4c81; color: white; animation: pulse 2s infinite; }
.timeline-item.rejected .timeline-icon { background: #ef4444; color: white; }
.timeline-content { text-align: center; }
.timeline-content strong { display: block; font-size: 12px; color: #1e293b; margin-bottom: 2px; }
.timeline-content span { font-size: 11px; color: #64748b; }
.pending-text { color: #94a3b8; font-style: italic; }

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(15, 76, 129, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(15, 76, 129, 0); }
}

/* Details Grid */
.details-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; margin-bottom: 24px; }
.detail-card { background: white; border-radius: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.06); overflow: hidden; }
.detail-card-header { display: flex; align-items: center; gap: 10px; padding: 16px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; color: #0f4c81; }
.detail-card-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
.detail-card-body { padding: 16px 20px; }
.detail-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f1f5f9; }
.detail-row:last-child { border-bottom: none; }
.detail-label { font-size: 13px; color: #64748b; }
.detail-value { font-size: 13px; font-weight: 600; color: #1e293b; text-align: right; max-width: 60%; }
.bill-number { color: #0f4c81; font-family: monospace; }
.text-success { color: #059669; }
.text-danger { color: #dc2626; }
.payment-status-badge { margin-top: 16px; padding: 10px; text-align: center; border-radius: 8px; font-size: 13px; font-weight: 600; }
.payment-status-badge.paid { background: #d1fae5; color: #065f46; }
.payment-status-badge.unpaid { background: #fee2e2; color: #991b1b; }

/* Workflow Card */
.workflow-card { background: white; border-radius: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.06); margin-bottom: 24px; overflow: hidden; }
.workflow-header { display: flex; align-items: center; gap: 10px; padding: 16px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; color: #0f4c81; }
.workflow-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
.workflow-timeline { padding: 20px; }
.workflow-item { display: flex; gap: 16px; padding-bottom: 20px; margin-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
.workflow-item:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.workflow-icon { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.workflow-item.approved .workflow-icon { background: #d1fae5; color: #059669; }
.workflow-item.rejected .workflow-icon { background: #fee2e2; color: #dc2626; }
.workflow-item.in_progress .workflow-icon { background: #dbeafe; color: #2563eb; }
.workflow-item.pending .workflow-icon { background: #fef3c7; color: #d97706; }
.workflow-content { flex: 1; }
.workflow-title { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
.workflow-title strong { font-size: 14px; color: #1e293b; }
.workflow-status { font-size: 11px; padding: 3px 8px; border-radius: 10px; font-weight: 600; }
.workflow-status.approved { background: #d1fae5; color: #065f46; }
.workflow-status.rejected { background: #fee2e2; color: #991b1b; }
.workflow-status.in_progress { background: #dbeafe; color: #1e40af; }
.workflow-status.pending { background: #fef3c7; color: #92400e; }
.workflow-comments { font-size: 13px; color: #64748b; margin: 4px 0; }
.workflow-assigned { font-size: 12px; color: #94a3b8; margin: 4px 0; }
.workflow-date { font-size: 12px; color: #94a3b8; }

/* Documents Card */
.documents-card { background: white; border-radius: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.06); margin-bottom: 24px; overflow: hidden; }
.documents-header { display: flex; align-items: center; gap: 10px; padding: 16px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; color: #0f4c81; }
.documents-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
.documents-grid { padding: 16px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.document-item { display: flex; align-items: center; gap: 12px; padding: 12px; background: #f8fafc; border-radius: 10px; }
.doc-icon { width: 40px; height: 40px; background: #e2e8f0; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #64748b; }
.doc-info { flex: 1; min-width: 0; }
.doc-info strong { display: block; font-size: 12px; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.doc-info span { font-size: 11px; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
.doc-status { font-size: 10px; padding: 3px 8px; border-radius: 10px; font-weight: 600; text-transform: capitalize; }
.doc-status.active { background: #d1fae5; color: #065f46; }
.doc-status.rejected { background: #fee2e2; color: #991b1b; }
.doc-status.archived { background: #e2e8f0; color: #64748b; }

/* Inspections Card */
.inspections-card { background: white; border-radius: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.06); margin-bottom: 24px; overflow: hidden; }
.inspections-header { display: flex; align-items: center; gap: 10px; padding: 16px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; color: #0f4c81; }
.inspections-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
.inspections-list { padding: 16px; }
.inspection-item { display: flex; gap: 16px; padding: 12px; background: #f8fafc; border-radius: 10px; margin-bottom: 12px; }
.inspection-item:last-child { margin-bottom: 0; }
.inspection-date { width: 50px; text-align: center; }
.inspection-date .day { display: block; font-size: 20px; font-weight: 700; color: #0f4c81; }
.inspection-date .month { display: block; font-size: 12px; color: #64748b; text-transform: uppercase; }
.inspection-info { flex: 1; }
.inspection-info strong { display: block; font-size: 14px; color: #1e293b; margin-bottom: 4px; }
.inspection-info span { display: block; font-size: 12px; color: #64748b; }
.inspection-status { display: inline-block; margin-top: 6px; font-size: 11px; padding: 3px 10px; border-radius: 10px; font-weight: 600; text-transform: capitalize; }
.inspection-status.scheduled { background: #dbeafe; color: #1e40af; }
.inspection-status.completed { background: #d1fae5; color: #065f46; }
.inspection-status.cancelled { background: #fee2e2; color: #991b1b; }
.inspection-status.rescheduled { background: #fef3c7; color: #92400e; }

/* Actions Card */
.actions-card { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 14px 24px; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; border: none; text-decoration: none; transition: all 0.2s; }
.btn-primary { background: #0f4c81; color: white; }
.btn-primary:hover { background: #0a3459; }
.btn-secondary { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #e2e8f0; }

.spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .search-input-group { flex-direction: column; }
  .details-grid { grid-template-columns: 1fr; }
  .documents-grid { grid-template-columns: 1fr; }
  .progress-timeline { gap: 4px; }
  .timeline-item { min-width: 70px; }
  .timeline-content strong { font-size: 10px; }
}
</style>